import { ApolloError } from '@apollo/client'
import { css } from '@emotion/react'
import { Flow, Text, TextInput, Theme, useTheme } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useErrorHandler } from 'components/error'
import { confirm } from 'components/modals/confirm'
import { useDeletarPoligonoTerritorioMutation } from 'graphql/hooks.generated'

import { TerritorioCamadaQueryModel } from '../../model-territorioMapa'
import { getDeleteCamadaMessage } from '../utils-territorioMapaCamadas'
import { CamadasListItem } from './CamadasListItem'

interface TerritorioMapaCamadasListProps {
  camadas: ReadonlyArray<TerritorioCamadaQueryModel>
  refetch: () => void
}

export function TerritorioMapaCamadasList(props: Readonly<TerritorioMapaCamadasListProps>) {
  const { camadas, refetch } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const alert = useAlert()
  const handleRejection = useErrorHandler()
  const [deletarPoligonoTerritorio] = useDeletarPoligonoTerritorioMutation()

  const handleDelete = async (camada: TerritorioCamadaQueryModel) => {
    return confirm({
      type: 'danger',
      title: 'Deseja excluir a camada?',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
      icon: {
        name: 'exclamationTriangleOutline',
        fill: 'alert',
      },
      body: <Text>{getDeleteCamadaMessage(camada)}</Text>,
      onConfirm: async () => {
        try {
          await deletarPoligonoTerritorio({ variables: { poligonoId: camada.id } })
          alert('success', 'Camada excluída com sucesso.')
          refetch()
          return
        } catch (error) {
          return handleRejection(error as ApolloError)
        }
      },
    })()
  }

  return (
    <Flow direction='vertical' alignItems='flex-start' gap={0} style={styles.root}>
      <div css={styles.inputWrapper}>
        <TextInput icon='zoomOutline' placeholder='Pesquisar por nome ou por visibilidade' />
      </div>
      <div css={styles.list}>
        <div css={styles.listContent}>
          {camadas.map((camada) => (
            <CamadasListItem key={camada.id} camada={camada} onDelete={() => handleDelete(camada)} />
          ))}
        </div>
      </div>
    </Flow>
  )
}

const createStyles = (theme: Theme) => ({
  root: css`
    margin: 0 -1rem;
    height: 100%;
    grid-template-rows: auto minmax(0, 1fr);

    & > div:not(:last-child) {
      border-bottom: 1px solid ${theme.pallete.divider};
    }
  `,
  inputWrapper: css`
    padding: 1rem;
  `,
  list: css`
    position: relative;
    height: 100%;
  `,
  listContent: css`
    position: absolute;
    inset: 0;
    overflow-y: auto;
    overflow-x: visible;

    & > div {
      border-bottom: 1px solid ${theme.pallete.divider};
    }
  `,
})
